import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import type { ReactNode } from "react";

import { TableHead } from "@/components/ui/table";
import { cn } from "@/lib/utils";

export type SortDir = "asc" | "desc";

// Shared sortable column header for data tables. Generic over the sort key
// so each page declares its own union of sortable column names. Renders as
// a styled `<TableHead>` so it composes naturally with the shadcn Table
// primitive — drop into any `<TableRow>` inside `<TableHeader>`.
//
// Click toggles asc/desc on the active column, or activates the new column
// with a sensible default direction (numbers / dates → desc; everything
// else → asc). Caller passes `activeKey` / `activeDir` so the indicator
// reflects the current state.
export function SortableHeader<K extends string>({
  label,
  sortKey,
  activeKey,
  activeDir,
  onSort,
  align = "left",
  className,
  filter,
}: {
  label: string;
  sortKey: K;
  activeKey: K;
  activeDir: SortDir;
  onSort: (key: K) => void;
  align?: "left" | "right" | "center";
  className?: string;
  // Optional column filter slot — renders a sibling control next to the
  // sort button so a click on the filter icon doesn't also trigger sort.
  filter?: ReactNode;
}) {
  const isActive = sortKey === activeKey;
  const ariaSort: "ascending" | "descending" | "none" = isActive
    ? activeDir === "asc"
      ? "ascending"
      : "descending"
    : "none";
  const indicatorClass = "ml-1 inline-block h-3 w-3";
  return (
    <TableHead
      aria-sort={ariaSort}
      className={cn(
        align === "right" && "text-right",
        align === "center" && "text-center",
        className,
      )}
    >
      <span
        className={cn("inline-flex items-center", align === "right" && "ml-auto flex-row-reverse")}
      >
        <button
          type="button"
          onClick={() => onSort(sortKey)}
          className={cn(
            "inline-flex items-center hover:text-foreground",
            isActive && "text-foreground",
          )}
        >
          {label}
          {isActive ? (
            activeDir === "asc" ? (
              <ArrowUp className={indicatorClass} aria-hidden />
            ) : (
              <ArrowDown className={indicatorClass} aria-hidden />
            )
          ) : (
            <ArrowUpDown className={cn(indicatorClass, "opacity-40")} aria-hidden />
          )}
        </button>
        {filter}
      </span>
    </TableHead>
  );
}

// Pure helper for the common toggle pattern: clicking the active column flips
// direction; clicking a new column activates it with the supplied default.
// Returns the next [key, dir] tuple.
export function nextSortState<K extends string>(
  current: { key: K; dir: SortDir },
  clicked: K,
  defaults: Partial<Record<K, SortDir>> = {},
): { key: K; dir: SortDir } {
  if (clicked === current.key) {
    return { key: clicked, dir: current.dir === "asc" ? "desc" : "asc" };
  }
  return { key: clicked, dir: defaults[clicked] ?? "asc" };
}
